<template>
  <div>
    <h1 class="main-header">
      <translate translate-context="email_templates">Email Templates</translate>
    </h1>

    <CCard class="no-borders">
      <CCardHeader>
        <div class="card-header-actions">
          <CButton
              v-if="canCreate"
              color="primary"
              :to="{ name: createRouteName }">
            <CIcon name="cil-plus" class="mr-1"/>
            <translate translate-context="email_templates">Customize Template</translate>
          </CButton>
        </div>
      </CCardHeader>

      <CCardBody class="px-0">
        <AjaxTable
            v-if="isReady"
            ref="ajaxTable"
            :fields="fields"
            :data="templates"
            :fetch="fetchEmailTemplates"
            table-name="emailTemplates"
            :context-key="contextKey"
            :sync-url="true"
        >
          <template #email_type="{item}">
            <td>
              <strong>{{ getEmailTypeLabel(item.email_type) }}</strong>
              <br>
              <small class="text-muted">{{ item.email_type }}</small>
            </td>
          </template>

          <template #delivery_service="{item}">
            <td>
              <CBadge :color="getServiceColor(item.delivery_service)">
                {{ getServiceLabel(item.delivery_service) }}
              </CBadge>
            </td>
          </template>

          <template #debug_mode="{item}">
            <td class="text-center">
              <CBadge :color="getDebugModeColor(item.debug_mode)">
                {{ getDebugModeLabel(item.debug_mode) }}
              </CBadge>
            </td>
          </template>

          <template #actions="{item}">
            <td class="text-right">
              <CButton
                  v-if="canEdit"
                  variant="outline"
                  color="primary"
                  :to="{ name: editRouteName, params: { emailType: item.email_type } }"
                  v-translate translate-context="list.actions">
                Edit
              </CButton>

              <CButton
                  v-if="canDelete"
                  variant="outline"
                  color="danger"
                  class="ml-2"
                  @click="onDelete(item)"
                  v-translate translate-context="list.actions">
                Delete
              </CButton>
            </td>
          </template>
        </AjaxTable>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import {mapGetters, mapActions} from 'vuex'
import {getDeliveryServiceColor, getDeliveryServiceLabel, getDeliveryServiceOptions} from '@/domain/core/constant/deliveryServices'
import AjaxTable from '@/domain/core/components/AjaxTable.vue'

export default {
  name: 'ListEmailTemplates',
  components: {
    AjaxTable
  },
  data() {
    return {
      isReady: false
    }
  },
  computed: {
    ...mapGetters('emailTemplates', ['templates', 'availableTypeByValue']),
    ...mapGetters('login', ['hasPerm']),

    scopeType() {
      return this.$route.meta.scopeType || 'global'
    },

    scopeId() {
      if (this.scopeType === 'pica-service') {
        return this.$route.params.picaServiceId
      }
      if (this.scopeType === 'contract-manager') {
        return this.$route.params.contractManagerId
      }
      if (this.scopeType === 'event-owner') {
        return this.$route.params.eventOwnerId
      }
      return null
    },

    contextKey() {
      if (this.scopeType === 'global') return 'global'
      return `${this.scopeType}-${this.scopeId}`
    },

    createRouteName() {
      const names = {
        'global': 'admin.emailTemplates.new',
        'contract-manager': 'contractManager.emailTemplates.new',
        'event-owner': 'eventOwner.emailTemplates.new',
        'pica-service': 'event.emailTemplates.new'
      }
      return names[this.scopeType]
    },

    editRouteName() {
      const names = {
        'global': 'admin.emailTemplates.edit',
        'contract-manager': 'contractManager.emailTemplates.edit',
        'event-owner': 'eventOwner.emailTemplates.edit',
        'pica-service': 'event.emailTemplates.edit'
      }
      return names[this.scopeType]
    },

    canCreate() {
      const perms = {
        'global': 'admin.email_template.create',
        'contract-manager': 'contract_manager.email_template.create',
        'event-owner': 'event_owner.email_template.create',
        'pica-service': 'email_template.create'
      }
      return this.hasPerm(perms[this.scopeType])
    },

    canEdit() {
      const perms = {
        'global': 'admin.email_template.edit',
        'contract-manager': 'contract_manager.email_template.edit',
        'event-owner': 'event_owner.email_template.edit',
        'pica-service': 'email_template.edit'
      }
      return this.hasPerm(perms[this.scopeType])
    },

    canDelete() {
      const perms = {
        'global': 'admin.email_template.delete',
        'contract-manager': 'contract_manager.email_template.delete',
        'event-owner': 'event_owner.email_template.delete',
        'pica-service': 'email_template.delete'
      }
      return this.hasPerm(perms[this.scopeType])
    },

    deliveryServiceFilterOptions() {
      return [
        {value: '', label: this.$pgettext('email_templates', 'All')},
        ...getDeliveryServiceOptions()
      ]
    },

    debugModeFilterOptions() {
      return [
        {value: '', label: this.$pgettext('email_templates', 'All')},
        {value: 'off', label: this.$pgettext('email_templates', 'Off')},
        {value: 'log_only', label: this.$pgettext('email_templates', 'Log Only')},
        {value: 'log_and_send', label: this.$pgettext('email_templates', 'Log & Send')}
      ]
    },

    fields() {
      return [
        {
          key: 'email_type',
          label: this.$pgettext('email_templates', 'Email Type'),
          _style: 'width: 35%',
          sorter: true
        },
        {
          key: 'delivery_service',
          label: this.$pgettext('email_templates', 'Delivery Service'),
          _style: 'width: 18%',
          sorter: true,
          options: this.deliveryServiceFilterOptions
        },
        {
          key: 'debug_mode',
          label: this.$pgettext('email_templates', 'Debug Mode'),
          _style: 'width: 12%',
          sorter: true,
          options: this.debugModeFilterOptions
        },
        {
          key: 'enabled',
          label: this.$pgettext('email_templates', 'Status'),
          _style: 'width: 10%',
          boolean: true,
          sorter: true
        },
        {
          key: 'actions',
          label: '',
          _style: 'width: 100px',
          filter: false,
          sorter: false
        }
      ]
    }
  },
  async mounted() {
    try {
      await this.setScope({type: this.scopeType, id: this.scopeId})

      const configTypes = {
        'global': 'email_templates_global',
        'contract-manager': 'email_templates_contract_manager',
        'event-owner': 'email_templates_event_owner',
        'pica-service': 'email_templates_pica_service'
      }

      await this.fetchConfig({
        type: configTypes[this.scopeType],
        id: this.scopeId
      })
    } catch (error) {
      this.notifyError(this.$pgettext('email_templates', 'Failed to load email templates'))
    }
    this.isReady = true
  },
  methods: {
    ...mapActions('emailTemplates', ['setScope', 'deleteEmailTemplate', 'fetchEmailTemplates']),
    ...mapActions('login', ['fetchConfig']),

    getEmailTypeLabel(emailType) {
      const emailTypeConfig = this.availableTypeByValue(emailType)
      return emailTypeConfig ? emailTypeConfig.label : emailType
    },

    getServiceColor(service) {
      return getDeliveryServiceColor(service)
    },

    getServiceLabel(service) {
      return getDeliveryServiceLabel(service)
    },

    getDebugModeColor(debugMode) {
      const colors = {
        'off': 'secondary',
        'log_only': 'warning',
        'log_and_send': 'info'
      }
      return colors[debugMode] || 'secondary'
    },

    getDebugModeLabel(debugMode) {
      const labels = {
        'off': this.$pgettext('email_templates', 'Off'),
        'log_only': this.$pgettext('email_templates', 'Log Only'),
        'log_and_send': this.$pgettext('email_templates', 'Log & Send')
      }
      return labels[debugMode] || debugMode
    },

    onDelete(item) {
      this.confirm({
        title: this.$pgettext('email_templates', 'Delete Template'),
        message: this.$pgettext('email_templates', 'Are you sure you want to delete this template configuration?'),
        cb_confirm: async () => {
          try {
            await this.deleteEmailTemplate(item.email_type)
            this.$refs.ajaxTable.fetchList()
            this.notifySuccess(this.$pgettext('email_templates', 'Template deleted successfully'))
          } catch (error) {
            this.notifyError(this.$pgettext('email_templates', 'Failed to delete template'))
          }
        }
      })
    }
  },
  watch: {
    async '$route'(to, from) {
      const newScopeType = to.meta.scopeType || 'global'
      const newScopeId = this.getScopeIdFromRoute(to)

      if (newScopeType !== this.scopeType || newScopeId !== this.scopeId) {
        this.isReady = false
        try {
          await this.setScope({type: newScopeType, id: newScopeId})

          const configTypes = {
            'global': 'email_templates_global',
            'contract-manager': 'email_templates_contract_manager',
            'event-owner': 'email_templates_event_owner',
            'pica-service': 'email_templates_pica_service'
          }

          await this.fetchConfig({
            type: configTypes[newScopeType],
            id: newScopeId
          })
        } catch (error) {
          this.notifyError(this.$pgettext('email_templates', 'Failed to load email templates'))
        }
        this.isReady = true
      }
    }
  },
  beforeMount() {
    this.getScopeIdFromRoute = (route) => {
      const scopeType = route.meta.scopeType || 'global'
      if (scopeType === 'pica-service') {
        return route.params.picaServiceId
      }
      if (scopeType === 'contract-manager') {
        return route.params.contractManagerId
      }
      if (scopeType === 'event-owner') {
        return route.params.eventOwnerId
      }
      return null
    }
  }
}
</script>

<style scoped>
.main-header {
  margin-bottom: 1.5rem;
}
</style>
